import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Stack,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import {
  useNavigate,
  useSearchParams,
  Link as RouterLink,
} from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import logo from "../../assets/logo_transparent_black.png";
import { resetPassword } from "../../api/user";
import { validatePassword } from "../../utils/passwordValidation";

export const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Check if token exists
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
      return;
    }

    // Validation
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!validatePassword(newPassword)) {
      setError("Password does not meet security requirements");
      return;
    }

    setIsLoading(true);

    try {
      const response = await resetPassword({
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      if (response.data?.status === "SUCCESS") {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setError(response.data?.message || "Failed to reset password");
      }
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(
        error.response?.data?.message ||
          "An error occurred while resetting password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container
      sx={{
        width: "100%",
        maxWidth: "450px !important",
        height: "85vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
          <img
            src={logo}
            alt="Fashion Words Logo"
            style={{
              maxWidth: "150px",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            backgroundColor: "primary.dark",
          }}
        >
          {success ? (
            // Success state
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  pt: 2,
                  pb: 4,
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    textAlign: "center",
                    color: "primary.light",
                    fontSize: "1.75rem",
                    fontWeight: "bold",
                    mb: 2,
                  }}
                >
                  Password Reset Successful
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    textAlign: "center",
                    color: "primary.light",
                    fontSize: "1rem",
                  }}
                >
                  Your password has been reset successfully. You can now login
                  with your new password.
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    color: "primary.light",
                    fontSize: "0.9rem",
                    mt: 2,
                  }}
                >
                  Redirecting to login...
                </Typography>
              </Box>

              <Button
                component={RouterLink}
                to="/login"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  mt: 2,
                  py: 1.5,
                }}
              >
                Go to Login
              </Button>
            </Box>
          ) : (
            // Form state
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  pt: 2,
                  pb: 4,
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    textAlign: "center",
                    color: "primary.light",
                    fontSize: "1.75rem",
                    fontWeight: "bold",
                    mb: 2,
                  }}
                >
                  Reset Password
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    textAlign: "center",
                    color: "primary.light",
                    fontSize: "1rem",
                  }}
                >
                  Enter your new password below.
                </Typography>
              </Box>
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <Box>
                    <TextField
                      fullWidth
                      id="new-password"
                      label="New Password"
                      type={showNewPassword ? "text" : "password"}
                      variant="filled"
                      required
                      disabled={!token || isLoading}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setError(null);
                      }}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() =>
                                  setShowNewPassword(!showNewPassword)
                                }
                                edge="end"
                              >
                                {showNewPassword ? (
                                  <VisibilityOffIcon />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "primary.light",
                        fontSize: "0.75rem",
                        mt: 0.5,
                        display: "block",
                      }}
                    >
                      Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 symbol
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    id="confirm-password"
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    variant="filled"
                    required
                    disabled={!token || isLoading}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError(null);
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  {error && (
                    <Typography
                      sx={{
                        color: "primary.light",
                        fontSize: "0.875rem",
                        textAlign: "center",
                        pb: 1,
                        borderBottom: "4px solid #D0021B",
                      }}
                    >
                      {error}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={!token || isLoading}
                    sx={{
                      mt: 2,
                      py: 1.5,
                    }}
                  >
                    {isLoading ? "Resetting Password..." : "Reset Password"}
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};
